import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { paymentsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import Navbar from '../components/Navbar';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [txn, setTxn] = useState(null);
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }
      try {
        const res = await paymentsAPI.status(sessionId);
        setTxn(res.data);
        if (res.data.payment_status === 'paid' || res.data.payment_status === 'completed') {
          setStatus('success');
          refreshUser();
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        attempts++;
        setTimeout(poll, pollInterval);
      } catch {
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };
    poll();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-[#020202] grid-bg" data-testid="payment-success-page">
      <Navbar />
      <div className="pt-32 px-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          {status === 'checking' && (
            <div data-testid="payment-checking">
              <Loader2 className="w-12 h-12 text-[#FF7A3D] animate-spin mx-auto mb-4" />
              <p className="font-heading text-xl font-bold text-white mb-2">VERIFYING PAYMENT</p>
              <p className="font-mono text-xs text-[#999]">Processing your transaction...</p>
            </div>
          )}
          {status === 'success' && (
            <div data-testid="payment-confirmed">
              <CheckCircle className="w-16 h-16 text-[#FF7A3D] mx-auto mb-4" />
              <p className="font-heading text-2xl font-bold text-white mb-2">PAYMENT CONFIRMED</p>
              <p className="font-mono text-sm text-[#888] mb-2">Credits have been added to your account</p>
              {txn && (
                <p className="font-heading text-3xl font-bold text-[#FF7A3D] mb-6">+{txn.credits} CREDITS</p>
              )}
              <div className="flex gap-3 justify-center">
                <Link to="/dashboard">
                  <Button className="bg-[#FF7A3D] text-black font-heading font-bold uppercase tracking-widest rounded-sm text-xs" data-testid="go-to-dashboard-btn">
                    DASHBOARD
                  </Button>
                </Link>
                <Link to="/marketplace">
                  <Button variant="outline" className="border-white/10 text-white hover:border-[#FF7A3D]/30 font-heading uppercase tracking-widest rounded-sm text-xs bg-transparent" data-testid="go-to-marketplace-btn">
                    MARKETPLACE
                  </Button>
                </Link>
              </div>
            </div>
          )}
          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <div data-testid="payment-failed">
              <XCircle className="w-16 h-16 text-[#ff003c] mx-auto mb-4" />
              <p className="font-heading text-2xl font-bold text-white mb-2">
                {status === 'timeout' ? 'VERIFICATION TIMEOUT' : 'PAYMENT ISSUE'}
              </p>
              <p className="font-mono text-sm text-[#888] mb-6">
                {status === 'timeout' ? 'Please check your dashboard for credit updates.' : 'Something went wrong with the payment.'}
              </p>
              <Link to="/pricing">
                <Button className="bg-[#FF7A3D] text-black font-heading font-bold uppercase tracking-widest rounded-sm text-xs" data-testid="retry-payment-btn">
                  TRY AGAIN
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
